<template>
  <v-container>
    <v-row>
      <v-col>
        <v-card flat>
          <v-card-title>
            Billing Member
            <v-spacer></v-spacer>
            <v-btn 
              color="info" 
              @click="printSelectedInvoices"
              :disabled="selectedInvoices.length === 0"
              class="mr-2"
            >
              <v-icon left>mdi-printer</v-icon>
              Print Selected ({{ selectedInvoices.length }})
            </v-btn>
            <v-btn 
              color="warning" 
              @click="openBulkStatusDialog"
              :disabled="selectedInvoices.length === 0"
              class="mr-2"
            >
              <v-icon left>mdi-update</v-icon>
              Update Status ({{ selectedInvoices.length }})
            </v-btn>
            <v-btn color="success" @click="openCreateInvoiceDialog">
              <v-icon left>mdi-plus</v-icon>
              New Invoice
            </v-btn>
          </v-card-title>

          <v-card-text>
            <v-row justify="end">
              <v-col cols="4" md="6" lg="2" class="py-0">
                <v-select 
                  :items="statusItems" 
                  v-model="statusFilter" 
                  label="Filter by Status"
                  clearable
                />
              </v-col>
            </v-row>

            <div>
              <v-data-table
                v-model="selectedInvoices"
                :loading="loading"
                :headers="headers"
                :items="filteredInvoices"
                :search="search"
                :footer-props="{ 'items-per-page-options': [15, -1] }"
                sort-by="SERVICE_DATE"
                sort-desc
                multi-sort
                loading-text="Loading data..."
                show-select
                item-key="INVOICE_ID"
                class="elevation-0 col-md-12 m-auto"
              >
                <template v-slot:item.AMOUNT="{ item }">
                  <span class="font-weight-bold">${{ item.AMOUNT.toFixed(2) }}</span>
                </template>

                <template v-slot:item.STATUS="{ item }">
                  <v-chip 
                    :color="getStatusColor(item.STATUS)" 
                    small
                    :text-color="getStatusTextColor(item.STATUS)"
                  >
                    {{ item.STATUS }}
                  </v-chip>
                </template>

                <template v-slot:item.SERVICE_DATE="{ item }">
                  {{ formatDate(item.SERVICE_DATE) }}
                </template>

                <template v-slot:item.DUE_DATE="{ item }">
                  <div>
                    {{ formatDate(item.DUE_DATE) }}
                    <v-chip 
                      v-if="isOverdue(item)"
                      x-small
                      color="error"
                      class="ml-2"
                    >
                      Overdue
                    </v-chip>
                  </div>
                </template>

                <template v-slot:item.Actions="{ item }">
                  <v-btn
                    icon
                    small
                    color="primary"
                    @click="viewInvoice(item)"
                    title="View Invoice"
                  >
                    <v-icon>mdi-eye</v-icon>
                  </v-btn>
                  <v-btn
                    icon
                    small
                    color="info"
                    @click="printInvoice(item)"
                    title="Print Invoice"
                  >
                    <v-icon>mdi-printer</v-icon>
                  </v-btn>
                  <v-btn
                    icon
                    small
                    color="success"
                    @click="openStatusDialog(item)"
                    title="Change Status"
                  >
                    <v-icon>mdi-update</v-icon>
                  </v-btn>
                  <v-btn
                    icon
                    small
                    color="warning"
                    @click="editInvoice(item)"
                    title="Edit Invoice"
                  >
                    <v-icon>mdi-pencil</v-icon>
                  </v-btn>
                  <v-btn
                    icon
                    small
                    color="error"
                    @click="deleteInvoice(item)"
                    title="Delete Invoice"
                  >
                    <v-icon>mdi-delete</v-icon>
                  </v-btn>
                </template>
              </v-data-table>
            </div>
          </v-card-text>
        </v-card>

        <!-- Create/Edit Invoice Dialog -->
        <v-dialog v-model="invoiceDialog" max-width="800px" persistent>
          <v-card>
            <v-toolbar dark color="primary">
              <v-toolbar-title>
                {{ isEditing ? 'Edit Invoice' : 'Create New Invoice' }}
              </v-toolbar-title>
              <v-spacer></v-spacer>
              <v-btn icon dark @click="closeInvoiceDialog">
                <v-icon>mdi-close</v-icon>
              </v-btn>
            </v-toolbar>

            <v-card-text class="pa-4">
              <v-form ref="invoiceForm" v-model="formValid" lazy-validation>
                <v-row>
                  <v-col cols="12" md="6">
                    <div class="text-body-1 mb-3">
                      Member Information:
                      
                      <!-- Member Search Dialog -->
                      <member-search-dialog
                        :user="user"
                        @memberSelected="saveSelectedMember"
                      ></member-search-dialog>

                      <!-- Selected Member Display -->
                      <v-card v-if="selectedMember" outlined class="mt-3 pa-3">
                        <div class="text-caption">ID: {{ selectedMember.memberID }}</div>
                        <div class="text-h6 font-weight-bold">
                          {{ selectedMember.name }}
                        </div>
                        <div class="text-body-2">
                          {{ selectedMember.birthDate }}
                        </div>
                      </v-card>

                      <div v-if="!selectedMember" class="px-1 text-subtitle-2 text-center pa-3">
                        Please select a member to continue
                      </div>
                    </div>
                  </v-col>

                  <v-col cols="12" md="6">
                    <v-text-field
                      v-model="invoiceData.AMOUNT"
                      label="Amount ($)"
                      type="number"
                      step="0.01"
                      min="0"
                      required
                      :rules="[v => !!v || 'Amount is required']"
                      outlined
                      dense
                    ></v-text-field>
                  </v-col>

                  <v-col cols="12">
                    <v-textarea
                      v-model="invoiceData.SERVICE_DESCRIPTION"
                      label="Service Description"
                      rows="3"
                      required
                      :rules="[v => !!v || 'Service description is required']"
                      outlined
                    ></v-textarea>
                  </v-col>

                  <v-col cols="12" md="6">
                    <v-menu
                      v-model="serviceDateMenu"
                      transition="scale-transition"
                      min-width="auto"
                      offset-y
                    >
                      <template v-slot:activator="{ on, attrs }">
                        <v-text-field
                          label="Service Date"
                          v-model="invoiceData.SERVICE_DATE"
                          required
                          :rules="[v => !!v || 'Service date is required']"
                          prepend-inner-icon="mdi-calendar"
                          outlined
                          dense
                          readonly
                          v-bind="attrs"
                          v-on="on"
                        ></v-text-field>
                      </template>
                      <v-date-picker v-model="invoiceData.SERVICE_DATE" no-title></v-date-picker>
                    </v-menu>
                  </v-col>

                  <v-col cols="12" md="6">
                    <v-menu
                      v-model="dueDateMenu"
                      transition="scale-transition"
                      min-width="auto"
                      offset-y
                    >
                      <template v-slot:activator="{ on, attrs }">
                        <v-text-field
                          label="Due Date"
                          v-model="invoiceData.DUE_DATE"
                          required
                          :rules="[v => !!v || 'Due date is required']"
                          prepend-inner-icon="mdi-calendar"
                          outlined
                          dense
                          readonly
                          v-bind="attrs"
                          v-on="on"
                        ></v-text-field>
                      </template>
                      <v-date-picker v-model="invoiceData.DUE_DATE" no-title></v-date-picker>
                    </v-menu>
                  </v-col>

                  <v-col cols="12" md="6">
                    <v-text-field
                      v-model="invoiceData.NOTES"
                      label="Notes (Optional)"
                      outlined
                      dense
                    ></v-text-field>
                  </v-col>
                </v-row>
              </v-form>
            </v-card-text>

            <v-card-actions>
              <v-spacer></v-spacer>
              <v-btn text @click="closeInvoiceDialog">Cancel</v-btn>
              <v-btn 
                color="primary" 
                @click="saveInvoice"
                :disabled="!formValid || !selectedMember"
                :loading="saving"
              >
                {{ isEditing ? 'Update' : 'Create' }} Invoice
              </v-btn>
            </v-card-actions>
          </v-card>
        </v-dialog>

        <!-- View Invoice Dialog -->
        <v-dialog v-model="viewDialog" max-width="600px">
          <v-card>
            <v-toolbar dark color="primary">
              <v-toolbar-title>Invoice Details</v-toolbar-title>
              <v-spacer></v-spacer>
              <v-btn 
                icon 
                dark 
                @click="printInvoice(selectedInvoice)"
                title="Print Invoice"
              >
                <v-icon>mdi-printer</v-icon>
              </v-btn>
              <v-btn icon dark @click="viewDialog = false">
                <v-icon>mdi-close</v-icon>
              </v-btn>
            </v-toolbar>

            <v-card-text class="pa-4" v-if="selectedInvoice">
              <v-row>
                <v-col cols="6">
                  <div class="text-caption">Invoice ID</div>
                  <div class="text-h6">{{ selectedInvoice.INVOICE_ID }}</div>
                </v-col>
                <v-col cols="6">
                  <div class="text-caption">Status</div>
                  <v-chip 
                    :color="getStatusColor(selectedInvoice.STATUS)" 
                    small
                    :text-color="getStatusTextColor(selectedInvoice.STATUS)"
                  >
                    {{ selectedInvoice.STATUS }}
                  </v-chip>
                </v-col>
                <v-col cols="6">
                  <div class="text-caption">Member ID</div>
                  <div class="text-body-1">{{ selectedInvoice.MEMBER_ID }}</div>
                </v-col>
                <v-col cols="6">
                  <div class="text-caption">Member Name</div>
                  <div class="text-body-1">{{ selectedInvoice.MEMBER_NAME }}</div>
                </v-col>
                <v-col cols="6">
                  <div class="text-caption">Amount</div>
                  <div class="text-h6 font-weight-bold">${{ selectedInvoice.AMOUNT.toFixed(2) }}</div>
                </v-col>
                <v-col cols="6">
                  <div class="text-caption">Due Date</div>
                  <div class="text-body-1">{{ formatDate(selectedInvoice.DUE_DATE) }}</div>
                </v-col>
                <v-col cols="12">
                  <div class="text-caption">Service Description</div>
                  <div class="text-body-1">{{ selectedInvoice.SERVICE_DESCRIPTION }}</div>
                </v-col>
                <v-col cols="12" v-if="selectedInvoice.NOTES">
                  <div class="text-caption">Notes</div>
                  <div class="text-body-1">{{ selectedInvoice.NOTES }}</div>
                </v-col>
                <v-col cols="6">
                  <div class="text-caption">Created By</div>
                  <div class="text-body-1">{{ selectedInvoice.CREATED_BY }}</div>
                </v-col>
                <v-col cols="6">
                  <div class="text-caption">Created Date</div>
                  <div class="text-body-1">{{ formatDate(selectedInvoice.CREATED_DATE) }}</div>
                </v-col>
              </v-row>
            </v-card-text>
          </v-card>
        </v-dialog>

        <!-- Delete Confirmation Dialog -->
        <v-dialog v-model="deleteDialog" max-width="400px">
          <v-card>
            <v-card-title>Delete Invoice?</v-card-title>
            <v-card-text>
              Are you sure you want to delete this invoice? This action cannot be undone.
            </v-card-text>
            <v-card-actions>
              <v-spacer></v-spacer>
              <v-btn text @click="deleteDialog = false">Cancel</v-btn>
              <v-btn color="error" @click="confirmDelete" :loading="deleting">Delete</v-btn>
            </v-card-actions>
          </v-card>
        </v-dialog>

        <!-- Individual Status Update Dialog -->
        <v-dialog v-model="statusDialog" max-width="500px">
          <v-card>
            <v-toolbar dark color="success">
              <v-toolbar-title>Change Invoice Status</v-toolbar-title>
              <v-spacer></v-spacer>
              <v-btn icon dark @click="statusDialog = false">
                <v-icon>mdi-close</v-icon>
              </v-btn>
            </v-toolbar>

            <v-card-text class="pa-4" v-if="selectedInvoice">
              <div class="mb-4">
                <h4>Invoice #{{ selectedInvoice.INVOICE_ID }}</h4>
                <p class="text-caption">Member: {{ selectedInvoice.MEMBER_NAME }}</p>
                <p class="text-caption">Current Status: 
                  <v-chip 
                    :color="getStatusColor(selectedInvoice.STATUS)" 
                    small
                    :text-color="getStatusTextColor(selectedInvoice.STATUS)"
                  >
                    {{ selectedInvoice.STATUS }}
                  </v-chip>
                </p>
              </div>

              <v-form ref="statusForm" v-model="statusFormValid">
                <v-select
                  v-model="newStatus"
                  :items="statusItems"
                  label="New Status"
                  required
                  :rules="[v => !!v || 'Status is required']"
                  outlined
                  dense
                >
                  <template v-slot:item="{ item: statusItem }">
                    <v-chip 
                      :color="getStatusColor(statusItem)" 
                      small
                      :text-color="getStatusTextColor(statusItem)"
                      class="ma-1"
                    >
                      {{ statusItem }}
                    </v-chip>
                  </template>
                </v-select>

                <v-textarea
                  v-model="statusNotes"
                  label="Notes (Optional)"
                  outlined
                  dense
                  rows="3"
                  placeholder="Add any notes about this status change..."
                ></v-textarea>

                <v-alert
                  v-if="newStatus === 'Paid'"
                  type="info"
                  class="mt-3"
                >
                  <strong>Note:</strong> When changing status to "Paid", the payment date will be set to today's date.
                </v-alert>
              </v-form>
            </v-card-text>

            <v-card-actions>
              <v-spacer></v-spacer>
              <v-btn text @click="statusDialog = false">Cancel</v-btn>
              <v-btn 
                color="success" 
                @click="confirmStatusUpdate" 
                :disabled="!statusFormValid"
                :loading="updatingStatus"
              >
                Update Status
              </v-btn>
            </v-card-actions>
          </v-card>
        </v-dialog>

        <!-- Bulk Status Update Dialog -->
        <v-dialog v-model="bulkStatusDialog" max-width="500px">
          <v-card>
            <v-toolbar dark color="warning">
              <v-toolbar-title>Update Status for {{ selectedInvoices.length }} Invoice(s)</v-toolbar-title>
              <v-spacer></v-spacer>
              <v-btn icon dark @click="bulkStatusDialog = false">
                <v-icon>mdi-close</v-icon>
              </v-btn>
            </v-toolbar>

            <v-card-text class="pa-4">
              <v-form ref="bulkStatusForm" v-model="bulkStatusFormValid">
                <v-select
                  v-model="bulkStatus"
                  :items="statusItems"
                  label="New Status"
                  required
                  :rules="[v => !!v || 'Status is required']"
                  outlined
                  dense
                >
                  <template v-slot:item="{ item: statusItem }">
                    <v-chip 
                      :color="getStatusColor(statusItem)" 
                      small
                      :text-color="getStatusTextColor(statusItem)"
                      class="ma-1"
                    >
                      {{ statusItem }}
                    </v-chip>
                  </template>
                </v-select>

                <v-textarea
                  v-model="bulkStatusNotes"
                  label="Notes (Optional)"
                  outlined
                  dense
                  rows="3"
                  placeholder="Add any notes about this status change..."
                ></v-textarea>

                <v-alert
                  v-if="bulkStatus === 'Paid'"
                  type="info"
                  class="mt-3"
                >
                  <strong>Note:</strong> When changing status to "Paid", the payment date will be set to today's date.
                </v-alert>
              </v-form>
            </v-card-text>

            <v-card-actions>
              <v-spacer></v-spacer>
              <v-btn text @click="bulkStatusDialog = false">Cancel</v-btn>
              <v-btn 
                color="warning" 
                @click="confirmBulkStatusUpdate" 
                :disabled="!bulkStatusFormValid"
                :loading="updatingBulkStatus"
              >
                Update {{ selectedInvoices.length }} Invoice(s)
              </v-btn>
            </v-card-actions>
          </v-card>
        </v-dialog>

        <!-- Success/Error Snackbar -->
        <v-snackbar v-model="snackbar" :color="snackbarColor" :timeout="3000">
          {{ snackbarMessage }}
        </v-snackbar>

        <!-- Invoice Print Component (Hidden) -->
        <div style="display: none;">
          <invoice-print 
            ref="invoicePrint" 
            :invoice="selectedInvoice" 
            v-if="selectedInvoice"
          />
        </div>

        <!-- Multiple Invoice Print Component (Hidden) -->
        <div style="display: none;">
          <multiple-invoice-print 
            ref="multipleInvoicePrint" 
            :invoices="selectedInvoices" 
            v-if="selectedInvoices.length > 0"
          />
        </div>

        <!-- Invoice Preview Dialog -->
        <invoice-preview 
          v-model="previewDialog"
          :invoice="selectedInvoice"
          v-if="selectedInvoice"
        />
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import axios from 'axios';
import MemberSearchDialog from '../components/auth-components/MemberSearchDialog.vue';
import InvoicePrint from '../components/billing/InvoicePrint.vue';
import MultipleInvoicePrint from '../components/billing/MultipleInvoicePrint.vue';
import InvoicePreview from '../components/billing/InvoicePreview.vue';

export default {
  name: 'BillingMember',
  components: {
    MemberSearchDialog,
    InvoicePrint,
    MultipleInvoicePrint,
    InvoicePreview,
  },
  data() {
    return {
      user: '',
      loading: false,
      saving: false,
      deleting: false,
      search: '',
      statusFilter: '',
      statusItems: ['Pending', 'Paid', 'Overdue', 'Cancelled'],
      invoices: [],
      selectedInvoices: [],
      invoiceDialog: false,
      viewDialog: false,
      previewDialog: false,
      deleteDialog: false,
      statusDialog: false,
      bulkStatusDialog: false,
      isEditing: false,
      formValid: false,
      statusFormValid: false,
      newStatus: '',
      statusNotes: '',
      updatingStatus: false,
      bulkStatusFormValid: false,
      bulkStatus: '',
      bulkStatusNotes: '',
      updatingBulkStatus: false,
      serviceDateMenu: false,
      dueDateMenu: false,
      selectedMember: null,
      selectedInvoice: null,
      invoiceToDelete: null,
      snackbar: false,
      snackbarMessage: '',
      snackbarColor: 'success',
      headers: [
        { text: 'Invoice ID', value: 'INVOICE_ID' },
        { text: 'Member Name', value: 'MEMBER_NAME' },
        { text: 'Amount', value: 'AMOUNT' },
        { text: 'Status', value: 'STATUS' },
        { text: 'Service Date', value: 'SERVICE_DATE' },
        { text: 'Due Date', value: 'DUE_DATE' },
        { text: 'Actions', value: 'Actions' },
      ],
      invoiceData: {
        AMOUNT: '',
        SERVICE_DESCRIPTION: '',
        SERVICE_DATE: '',
        DUE_DATE: '',
        NOTES: ''
      }
    };
  },
  computed: {
    filteredInvoices() {
      if (!this.statusFilter) {
        return this.invoices;
      }
      return this.invoices.filter(invoice => invoice.STATUS === this.statusFilter);
    }
  },
  methods: {
    async getUser() {
      try {
        const response = await axios.get('/api/user');
        this.user = response.data[0];
        
        // Check if user is Superuser
        if (this.user.role !== 'Superuser') {
          this.$router.push('/');
          return;
        }
        
        this.getInvoices();
      } catch (error) {
        console.error('Error getting user:', error);
        this.$router.push('/login');
      }
    },

    async getInvoices() {
      this.loading = true;
      try {
        const response = await axios.get('/api/billing/invoices');
        this.invoices = response.data;
      } catch (error) {
        console.error('Error fetching invoices:', error);
        this.showSnackbar('Error fetching invoices', 'error');
      } finally {
        this.loading = false;
      }
    },

    openCreateInvoiceDialog() {
      this.isEditing = false;
      this.invoiceDialog = true;
      this.resetInvoiceData();
    },

    editInvoice(invoice) {
      this.isEditing = true;
      this.selectedInvoice = invoice;
      this.invoiceData = {
        AMOUNT: invoice.AMOUNT.toString(),
        SERVICE_DESCRIPTION: invoice.SERVICE_DESCRIPTION,
        SERVICE_DATE: this.formatDateForInput(invoice.SERVICE_DATE),
        DUE_DATE: this.formatDateForInput(invoice.DUE_DATE),
        NOTES: invoice.NOTES || ''
      };
      this.selectedMember = {
        memberID: invoice.MEMBER_ID,
        name: invoice.MEMBER_NAME,
        birthDate: this.formatDate(invoice.MEMBER_DOB)
      };
      this.invoiceDialog = true;
    },

    viewInvoice(invoice) {
      this.selectedInvoice = invoice;
      this.previewDialog = true;
    },

    deleteInvoice(invoice) {
      this.invoiceToDelete = invoice;
      this.deleteDialog = true;
    },

    async confirmDelete() {
      this.deleting = true;
      try {
        await axios.delete(`/api/billing/invoices/${this.invoiceToDelete.INVOICE_ID}`);
        this.invoices = this.invoices.filter(inv => inv.INVOICE_ID !== this.invoiceToDelete.INVOICE_ID);
        this.showSnackbar('Invoice deleted successfully', 'success');
        this.deleteDialog = false;
      } catch (error) {
        console.error('Error deleting invoice:', error);
        this.showSnackbar('Error deleting invoice', 'error');
      } finally {
        this.deleting = false;
      }
    },

    saveSelectedMember(member) {
      this.selectedMember = member;
    },

    async saveInvoice() {
      if (this.$refs.invoiceForm && this.$refs.invoiceForm.validate()) {
        this.saving = true;
        try {
          const invoiceData = {
            MEMBER_ID: this.selectedMember.memberID,
            MEMBER_NAME: this.selectedMember.name,
            MEMBER_DOB: this.selectedMember.birthDate,
            SERVICE_DESCRIPTION: this.invoiceData.SERVICE_DESCRIPTION,
            SERVICE_DATE: this.invoiceData.SERVICE_DATE,
            AMOUNT: parseFloat(this.invoiceData.AMOUNT),
            DUE_DATE: this.invoiceData.DUE_DATE,
            NOTES: this.invoiceData.NOTES
          };

          if (this.isEditing) {
            const response = await axios.put(`/api/billing/invoices/${this.selectedInvoice.INVOICE_ID}`, invoiceData);
            
            // Update the invoice in the local array
            const invoiceIndex = this.invoices.findIndex(inv => inv.INVOICE_ID === this.selectedInvoice.INVOICE_ID);
            if (invoiceIndex !== -1) {
              // Update all fields with the response data
              this.invoices[invoiceIndex] = {
                ...this.invoices[invoiceIndex],
                MEMBER_ID: invoiceData.MEMBER_ID,
                MEMBER_NAME: invoiceData.MEMBER_NAME,
                MEMBER_DOB: new Date(invoiceData.MEMBER_DOB),
                SERVICE_DESCRIPTION: invoiceData.SERVICE_DESCRIPTION,
                SERVICE_DATE: new Date(invoiceData.SERVICE_DATE),
                AMOUNT: invoiceData.AMOUNT,
                DUE_DATE: new Date(invoiceData.DUE_DATE),
                NOTES: invoiceData.NOTES
              };
            }
            
            this.showSnackbar('Invoice updated successfully', 'success');
          } else {
            const response = await axios.post('/api/billing/invoices', invoiceData);
            this.invoices.unshift(response.data); // Add new invoice to the beginning of the array
            this.showSnackbar('Invoice created successfully', 'success');
          }

          this.closeInvoiceDialog();
        } catch (error) {
          console.error('Error saving invoice:', error);
          this.showSnackbar('Error saving invoice', 'error');
        } finally {
          this.saving = false;
        }
      }
    },

    closeInvoiceDialog() {
      this.invoiceDialog = false;
      this.isEditing = false;
      this.selectedMember = null;
      this.resetInvoiceData();
    },

    resetInvoiceData() {
      this.invoiceData = {
        AMOUNT: '',
        SERVICE_DESCRIPTION: '',
        SERVICE_DATE: '',
        DUE_DATE: '',
        NOTES: ''
      };
    },

    getStatusColor(status) {
      const colors = {
        'Pending': 'orange',
        'Paid': 'green',
        'Overdue': 'red',
        'Cancelled': 'grey'
      };
      return colors[status] || 'grey';
    },

    getStatusTextColor(status) {
      return 'white';
    },

    formatDate(date) {
      if (!date) return '';
      return new Date(date).toLocaleDateString();
    },

    formatDateForInput(date) {
      if (!date) return '';
      return new Date(date).toISOString().split('T')[0];
    },

    showSnackbar(message, color) {
      this.snackbarMessage = message;
      this.snackbarColor = color;
      this.snackbar = true;
    },

    printInvoice(invoice) {
      if (!invoice) return;
      
      this.selectedInvoice = invoice;
      this.$nextTick(() => {
        if (this.$refs.invoicePrint) {
          this.$refs.invoicePrint.print();
        }
      });
    },

    printSelectedInvoices() {
      if (this.selectedInvoices.length === 0) {
        this.showSnackbar('Please select invoices to print', 'warning');
        return;
      }

      this.showSnackbar(`Preparing to print ${this.selectedInvoices.length} invoice(s) as a single PDF`, 'info');

      // Wait for Vue to update the DOM and then print
      this.$nextTick(() => {
        if (this.$refs.multipleInvoicePrint) {
          this.$refs.multipleInvoicePrint.print();
        }
      });
    },

    openStatusDialog(invoice) {
      this.selectedInvoice = invoice;
      this.newStatus = invoice.STATUS;
      this.statusNotes = '';
      this.statusDialog = true;
    },

    async confirmStatusUpdate() {
      if (this.$refs.statusForm && this.$refs.statusForm.validate()) {
        if (this.newStatus === this.selectedInvoice.STATUS) {
          this.showSnackbar('Status is already set to this value', 'warning');
          return;
        }

        this.updatingStatus = true;
        
        try {
          const updateData = { STATUS: this.newStatus };
          
          // If status is being changed to 'Paid', add current date as PAID_DATE
          if (this.newStatus === 'Paid' && this.selectedInvoice.STATUS !== 'Paid') {
            updateData.PAID_DATE = new Date().toISOString();
          }

          // Add notes if provided
          if (this.statusNotes.trim()) {
            updateData.NOTES = this.selectedInvoice.NOTES 
              ? `${this.selectedInvoice.NOTES}\n\nStatus changed to ${this.newStatus} on ${new Date().toLocaleDateString()}: ${this.statusNotes}`
              : `Status changed to ${this.newStatus} on ${new Date().toLocaleDateString()}: ${this.statusNotes}`;
          }

          await axios.put(`/api/billing/invoices/${this.selectedInvoice.INVOICE_ID}`, updateData);
          
          // Update the invoice in the local array
          const invoiceIndex = this.invoices.findIndex(inv => inv.INVOICE_ID === this.selectedInvoice.INVOICE_ID);
          if (invoiceIndex !== -1) {
            this.invoices[invoiceIndex].STATUS = this.newStatus;
            if (this.newStatus === 'Paid' && this.selectedInvoice.STATUS !== 'Paid') {
              this.invoices[invoiceIndex].PAID_DATE = updateData.PAID_DATE;
            }
            if (this.statusNotes.trim()) {
              this.invoices[invoiceIndex].NOTES = updateData.NOTES;
            }
          }

          this.showSnackbar(`Invoice status updated to ${this.newStatus}`, 'success');
          this.statusDialog = false;
        } catch (error) {
          console.error('Error updating invoice status:', error);
          this.showSnackbar('Error updating invoice status', 'error');
        } finally {
          this.updatingStatus = false;
        }
      }
    },

    openBulkStatusDialog() {
      if (this.selectedInvoices.length === 0) {
        this.showSnackbar('Please select invoices to update', 'warning');
        return;
      }
      this.bulkStatus = '';
      this.bulkStatusNotes = '';
      this.bulkStatusDialog = true;
    },

    async confirmBulkStatusUpdate() {
      if (this.$refs.bulkStatusForm && this.$refs.bulkStatusForm.validate()) {
        this.updatingBulkStatus = true;
        
        try {
          const updatePromises = this.selectedInvoices.map(async (invoice) => {
            const updateData = { STATUS: this.bulkStatus };
            
            // If status is being changed to 'Paid', add current date as PAID_DATE
            if (this.bulkStatus === 'Paid' && invoice.STATUS !== 'Paid') {
              updateData.PAID_DATE = new Date().toISOString();
            }

            // Add notes if provided
            if (this.bulkStatusNotes.trim()) {
              updateData.NOTES = invoice.NOTES 
                ? `${invoice.NOTES}\n\nStatus changed to ${this.bulkStatus} on ${new Date().toLocaleDateString()}: ${this.bulkStatusNotes}`
                : `Status changed to ${this.bulkStatus} on ${new Date().toLocaleDateString()}: ${this.bulkStatusNotes}`;
            }

            return axios.put(`/api/billing/invoices/${invoice.INVOICE_ID}`, updateData);
          });

          await Promise.all(updatePromises);
          
          // Update local data
          this.selectedInvoices.forEach(invoice => {
            const invoiceIndex = this.invoices.findIndex(inv => inv.INVOICE_ID === invoice.INVOICE_ID);
            if (invoiceIndex !== -1) {
              this.invoices[invoiceIndex].STATUS = this.bulkStatus;
              if (this.bulkStatus === 'Paid' && invoice.STATUS !== 'Paid') {
                this.invoices[invoiceIndex].PAID_DATE = new Date().toISOString();
              }
              if (this.bulkStatusNotes.trim()) {
                this.invoices[invoiceIndex].NOTES = this.invoices[invoiceIndex].NOTES 
                  ? `${this.invoices[invoiceIndex].NOTES}\n\nStatus changed to ${this.bulkStatus} on ${new Date().toLocaleDateString()}: ${this.bulkStatusNotes}`
                  : `Status changed to ${this.bulkStatus} on ${new Date().toLocaleDateString()}: ${this.bulkStatusNotes}`;
              }
            }
          });

          this.showSnackbar(`Updated ${this.selectedInvoices.length} invoice(s) to ${this.bulkStatus}`, 'success');
          this.bulkStatusDialog = false;
          this.selectedInvoices = [];
        } catch (error) {
          console.error('Error updating bulk status:', error);
          this.showSnackbar('Error updating invoice statuses', 'error');
        } finally {
          this.updatingBulkStatus = false;
        }
      }
    },

    isOverdue(invoice) {
      if (!invoice.DUE_DATE || invoice.STATUS === 'Paid' || invoice.STATUS === 'Cancelled') return false;
      const dueDate = new Date(invoice.DUE_DATE);
      const now = new Date();
      return dueDate < now;
    }
  },
  mounted() {
    this.getUser();
  }
};
</script>

<style scoped>
.v-data-table {
  box-shadow: none;
}
</style>
